/*
 * ─────────────────────────────────────────────────────────────────
 * Barra de navegación inferior para móvil (Capacitor / Android / iOS).
 *
 * Mejoras respecto a la versión anterior:
 *  - useMediaQuery en lugar de window.innerWidth (reactivo a resize/rotación)
 *  - Área táctil mínima de 56 px por tab (guía HIG Apple + Material)
 *  - Indicador activo con pill animado
 *  - Haptic feedback al cambiar de tab (@capacitor/haptics)
 *  - FAB central sin animación pulse continua (solo al pulsar)
 *  - Safe area correcta en barra inferior
 *  - Header móvil extraído aquí mismo para tenerlo centralizado
 * ─────────────────────────────────────────────────────────────────
 */
import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { Box, Paper, IconButton, Typography, Fab, Tooltip } from "@mui/material";

// MUI Icons
import ListAltIcon        from "@mui/icons-material/ListAlt";
import QrCodeScannerIcon  from "@mui/icons-material/QrCodeScanner";
import CompareArrowsIcon  from "@mui/icons-material/CompareArrows";
import ReceiptLongIcon    from "@mui/icons-material/ReceiptLong";
import PersonIcon         from "@mui/icons-material/Person";
import LogoutIcon         from "@mui/icons-material/LogoutOutlined";

// Capacitor Haptics (si no está instalado, el catch lo absorbe silenciosamente)
let Haptics, ImpactStyle;
try {
    const cap = require("@capacitor/haptics");
    Haptics     = cap.Haptics;
    ImpactStyle = cap.ImpactStyle;
} catch (_) {
    // Entorno web sin plugin nativo — no pasa nada
}

/**
 * Dispara un haptic feedback ligero si el plugin está disponible.
 * En web simplemente no hace nada.
 */
async function hapticLight() {
    try {
        if (Haptics) await Haptics.impact({ style: ImpactStyle?.Light ?? "LIGHT" });
    } catch (_) {}
}

/**
 * Definición de los 4 tabs principales.
 * El botón central FAB (Comparar) NO es un tab — vive fuera del array.
 */
const TABS = [
    { label: "Listas",    value: "/listas",    icon: ListAltIcon       },
    { label: "Saludable", value: "/scan",       icon: QrCodeScannerIcon },
    // hueco central — se rellena con el FAB
    { label: "Tickets",   value: "/admin/ocr",  icon: ReceiptLongIcon   },
    { label: "Perfil",    value: "/profile",    icon: PersonIcon        },
];

export default function MobileBottomNavigation({ onLogout }) {
    const location  = useLocation();
    const nav       = useNavigate();
    const theme     = useTheme();

    /**
     * useMediaQuery es reactivo: si el usuario rota el móvil o
     * redimensiona la ventana, el componente se re-renderiza solo.
     * Reemplaza el estático window.innerWidth < 900.
     */
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    /**
     * Navega a una ruta con haptic feedback.
     * IMPORTANTE: debe declararse ANTES del return condicional —
     * los hooks no pueden llamarse después de un return (regla de hooks de React).
     */
    const irA = useCallback(async (ruta) => {
        await hapticLight();
        nav(ruta);
    }, [nav]);

    /**
     * Determina si un tab está activo.
     * Usa startsWith para que rutas hijas (p.ej. /listas/123) también activen el tab.
     * También debe ir antes del return condicional por la misma razón.
     */
    const esActivo = (ruta) => location.pathname.startsWith(ruta);

    // No renderizar nada en escritorio — el return condicional va AL FINAL de los hooks
    if (!isMobile) return null;

    return (
        <>
            {/* ── HEADER MÓVIL ──────────────────────────────────────── */}
            <Box
                component="header"
                sx={{
                    position:    "fixed",
                    top:         0,
                    left:        0,
                    right:       0,
                    // Altura base 56px + safe area del notch
                    height:      "calc(56px + env(safe-area-inset-top))",
                    bgcolor:     "background.paper",
                    display:     "flex",
                    alignItems:  "flex-end",
                    justifyContent: "space-between",
                    px:          2,
                    pb:          1,
                    pt:          "env(safe-area-inset-top)",
                    zIndex:      1100,
                    boxShadow:   "0 1px 0 rgba(0,0,0,0.08)",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 900,
                        color:      "primary.main",
                        fontSize:   "1.25rem",
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                    }}
                >
                    ComPrara
                </Typography>

                <Tooltip title="Cerrar sesión" arrow>
                    <IconButton
                        onClick={onLogout}
                        size="medium"
                        aria-label="Cerrar sesión"
                        sx={{
                            color:   "error.main",
                            bgcolor: "error.50",
                            // Área táctil mínima 44×44 px (HIG)
                            minWidth:  44,
                            minHeight: 44,
                            "&:hover": { bgcolor: "error.100" },
                        }}
                    >
                        <LogoutIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── BOTTOM BAR ────────────────────────────────────────── */}
            <Paper
                component="nav"
                elevation={0}
                sx={{
                    position:     "fixed",
                    bottom:       0,
                    left:         0,
                    right:        0,
                    zIndex:       1000,
                    bgcolor:      "background.paper",
                    borderTop:    "1px solid rgba(0,0,0,0.07)",
                    borderRadius: "20px 20px 0 0",
                    // Añade padding para la barra de inicio de iOS/Android
                    pb:           "env(safe-area-inset-bottom)",
                    boxShadow:    "0 -4px 24px rgba(0,0,0,0.08)",
                }}
            >
                {/*
                 * Usamos flexbox manual en lugar del componente BottomNavigation
                 * de MUI porque necesitamos insertar el hueco del FAB sin romper
                 * el sistema de "value activo".
                 */}
                <Box
                    sx={{
                        display:        "flex",
                        alignItems:     "stretch",
                        justifyContent: "space-around",
                        height:         64,   // área táctil generosa
                        px:             1,
                    }}
                >
                    {/* Tabs izquierdos: Listas + Saludable */}
                    {TABS.slice(0, 2).map((tab) => (
                        <TabButton
                            key={tab.value}
                            tab={tab}
                            activo={esActivo(tab.value)}
                            onClick={() => irA(tab.value)}
                        />
                    ))}

                    {/* Hueco central para el FAB — no es interactivo */}
                    <Box sx={{ width: 72, flexShrink: 0 }} aria-hidden="true" />

                    {/* Tabs derechos: Tickets + Perfil */}
                    {TABS.slice(2).map((tab) => (
                        <TabButton
                            key={tab.value}
                            tab={tab}
                            activo={esActivo(tab.value)}
                            onClick={() => irA(tab.value)}
                        />
                    ))}
                </Box>
            </Paper>

            {/* ── FAB CENTRAL (Comparar) ────────────────────────────── */}
            <Box
                sx={{
                    position:  "fixed",
                    /*
                     * Centrado verticalmente EN la barra:
                     * bottom = safe-area + mitad de la barra (32px = 64px/2)
                     * El FAB queda a caballo entre el contenido y la barra,
                     * sin sobresalir como "media luna" por encima.
                     */
                    bottom:    "calc(32px + env(safe-area-inset-bottom))",
                    left:      "50%",
                    transform: "translateX(-50%)",
                    zIndex:    1001,
                }}
            >
                <Tooltip title="Comparar precios" arrow placement="top">
                    <Fab
                        color="primary"
                        aria-label="Comparar precios"
                        onClick={() => irA("/compare")}
                        sx={{
                            width:       52,
                            height:      52,
                            boxShadow:   "0 4px 16px rgba(0,0,0,0.25)",
                            border:      "3px solid",
                            borderColor: "background.paper",
                            transition:  "transform 0.15s ease, box-shadow 0.15s ease",
                            "&:active": {
                                transform: "scale(0.92)",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            },
                            // Resalte si está en /compare
                            ...(location.pathname.startsWith("/compare") && {
                                boxShadow: `0 4px 16px ${theme.palette.primary.main}55`,
                            }),
                        }}
                    >
                        <CompareArrowsIcon sx={{ fontSize: 24 }} />
                    </Fab>
                </Tooltip>
            </Box>
        </>
    );
}

/* ─────────────────────────────────────────────────────────────────
 * TabButton
 * Subcomponente para cada tab individual.
 * Separa la responsabilidad para mantener el componente principal limpio.
 * ───────────────────────────────────────────────────────────────── */
function TabButton({ tab, activo, onClick }) {
    const IconComp = tab.icon;

    return (
        <Box
            component="button"
            onClick={onClick}
            aria-label={tab.label}
            aria-current={activo ? "page" : undefined}
            sx={{
                // Reset de estilos de botón nativo
                background:  "none",
                border:      "none",
                cursor:      "pointer",
                outline:     "none",
                // Layout
                flex:        1,
                display:     "flex",
                flexDirection: "column",
                alignItems:  "center",
                justifyContent: "center",
                gap:         "3px",
                // Área táctil mínima 56×56 px — accesibilidad táctil
                minHeight:   56,
                minWidth:    44,
                // Transición suave de color
                transition:  "color 0.2s ease",
                color: activo ? "primary.main" : "text.secondary",
                // Feedback al pulsar
                "&:active": {
                    transform: "scale(0.9)",
                },
                WebkitTapHighlightColor: "transparent",  // quita el flash azul en Android
            }}
        >
            {/* Contenedor del icono con pill de fondo cuando está activo */}
            <Box
                sx={{
                    position:     "relative",
                    display:      "flex",
                    alignItems:   "center",
                    justifyContent: "center",
                    width:        40,
                    height:       28,
                    borderRadius: 999,
                    // Pill de fondo animado: visible solo en tab activo
                    bgcolor: activo ? "primary.main" : "transparent",
                    transition:   "background-color 0.25s ease, width 0.25s ease",
                    color:        activo ? "primary.contrastText" : "inherit",
                }}
            >
                <IconComp sx={{ fontSize: 22 }} />
            </Box>

            {/* Etiqueta del tab */}
            <Typography
                variant="caption"
                sx={{
                    fontSize:    "0.68rem",
                    fontWeight:  activo ? 700 : 400,
                    lineHeight:  1,
                    // Color heredado del padre (cambia con activo)
                    color:       "inherit",
                    transition:  "font-weight 0.2s ease",
                    userSelect:  "none",
                }}
            >
                {tab.label}
            </Typography>
        </Box>
    );
}